/**
 * useCuratorApi - HyDE Search + Anchor-Based Retrieval Hook
 *
 * Routes to Electron IPC or cloud API depending on platform.
 *
 * @module @humanizer/studio/hooks/useCuratorApi
 */

import { useCallback, useMemo } from 'react';
import type {
  CuratorSearchResult,
  CollectionCriteria,
} from '../types/electron';

const isElectron = typeof window !== 'undefined' && !!window.isElectron;

export interface CuratorApi {
  synthesize: (query: string, opts?: {
    conversationContext?: string;
    collectionName?: string;
  }) => Promise<{ anchorPassage: string; durationMs: number }>;
  searchWithAnchor: (anchorPassage: string, opts?: {
    limit?: number;
    sourceTypes?: string[];
    collectionCriteria?: CollectionCriteria;
  }) => Promise<CuratorSearchResult[]>;
  refineQuery: (originalQuery: string, previousAnchor: string, refinement: string) => Promise<{
    anchorPassage: string;
    durationMs: number;
  }>;
}

export function useCuratorApi(): CuratorApi {
  const synthesize = useCallback(async (query: string, opts?: any) => {
    if (isElectron && window.electronCurator) {
      return window.electronCurator.synthesize(query, opts);
    }
    throw new Error('Curator not available on this platform');
  }, []);

  const searchWithAnchor = useCallback(async (anchorPassage: string, opts?: any) => {
    if (isElectron && window.electronCurator) {
      return window.electronCurator.searchWithAnchor(anchorPassage, opts);
    }
    throw new Error('Curator not available on this platform');
  }, []);

  const refineQuery = useCallback(async (originalQuery: string, previousAnchor: string, refinement: string) => {
    if (isElectron && window.electronCurator) {
      return window.electronCurator.refineQuery(originalQuery, previousAnchor, refinement);
    }
    throw new Error('Curator not available on this platform');
  }, []);

  return useMemo(() => ({
    synthesize,
    searchWithAnchor,
    refineQuery,
  }), [synthesize, searchWithAnchor, refineQuery]);
}
